/**
 * Passenger Validation Utilities for Tripjack Booking
 */

export const validateName = (name) => {
  // Only alphabets and spaces allowed
  const nameRegex = /^[A-Za-z\s]+$/;
  return nameRegex.test(name) && name.trim().length >= 2;
};

export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone) => {
  // Accepts E.164 (from <PhoneInput>) or legacy 10-digit Indian numbers
  // for backward compatibility with any form still on the old schema.
  if (!phone) return false;
  const cleaned = String(phone).replace(/[\s-]/g, '');
  if (cleaned.startsWith('+')) {
    try {
      // Defer to libphonenumber-js used by <PhoneInput>
      // eslint-disable-next-line global-require
      const { isValidPhoneNumber } = require('libphonenumber-js');
      return isValidPhoneNumber(cleaned);
    } catch { return /^\+\d{8,15}$/.test(cleaned); }
  }
  const phoneRegex = /^[6-9]\d{9}$/;
  return phoneRegex.test(cleaned);
};

export const validatePassport = (passport) => {
  // Alphanumeric, 6-15 characters
  const passportRegex = /^[A-Z0-9]{6,15}$/i;
  return passportRegex.test(passport);
};

export const validateGSTNumber = (gstNo) => {
  // GST format: 22AAAAA0000A1Z5 (15 characters)
  const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  return gstRegex.test(gstNo);
};

export const validateDOB = (dob, passengerType) => {
  if (!dob) {
    // DOB mandatory for CHILD and INFANT
    return passengerType === 'ADULT';
  }
  
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  // Age validation per passenger type
  if (passengerType === 'ADULT') return age >= 12;
  if (passengerType === 'CHILD') return age >= 2 && age < 12;
  if (passengerType === 'INFANT') return age < 2;
  
  return false;
};

/**
 * Normalise API / Mongo date values for <input type="date" /> — the value must
 * be exactly YYYY-MM-DD; ISO strings with a time part are rejected and show empty.
 */
export const toHTMLDateInputValue = (raw) => {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return '';
  }
  if (typeof raw === 'object' && raw !== null && typeof raw.toISOString === 'function') {
    return raw.toISOString().slice(0, 10);
  }
  const s = String(raw).trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  return '';
};

export const validatePassportExpiry = (expiryDate, travelDate) => {
  if (!expiryDate || !travelDate) return false;
  
  const expiry = new Date(expiryDate);
  const travel = new Date(travelDate);
  
  // Passport must be valid for at least 6 months from travel date
  const sixMonthsLater = new Date(travel);
  sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
  
  return expiry > sixMonthsLater;
};

export const getPassengerTypeFromAge = (dob) => {
  if (!dob) return 'ADULT';
  
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  if (age < 2) return 'INFANT';
  if (age < 12) return 'CHILD';
  return 'ADULT';
};

export const getTitleOptions = (passengerType) => {
  if (passengerType === 'ADULT') {
    return ['Mr', 'Mrs', 'Ms'];
  } else if (passengerType === 'CHILD') {
    return ['Master', 'Ms'];
  } else {
    return ['Master', 'Ms'];
  }
};

/**
 * Derive form-level requirements from the Tripjack review response.
 *
 * `formConditions` arrives from the backend `/api/tripjack/review` endpoint
 * under `form_conditions` — it tells us, for THIS specific fare, which
 * passenger fields are mandatory. Fallback to sensible defaults when absent
 * (domestic, DOB for child/infant only, passport never required, GST optional).
 */
export const deriveFieldRequirements = (formConditions, passengerType) => {
  const pt = (passengerType || 'ADULT').toLowerCase();
  const isInfant = pt === 'infant';
  const isChild = pt === 'child';

  // Defaults (used when backend didn't surface conditions)
  const defaults = {
    dobRequired: isInfant || isChild,  // historical behaviour
    passportRequired: false,
    gstMandatory: false,
    gstApplicable: true,
    international: false,
    seatAllowed: true,
    baggageAllowed: true,
    mealAllowed: true,
  };
  if (!formConditions || typeof formConditions !== 'object') return defaults;

  const dobMap = formConditions.dob_required || {};
  const ppMap = formConditions.passport_required || {};

  return {
    dobRequired:
      dobMap[pt] !== undefined ? !!dobMap[pt] : defaults.dobRequired,
    passportRequired:
      ppMap[pt] !== undefined ? !!ppMap[pt] : defaults.passportRequired,
    gstMandatory: !!formConditions.gst_mandatory,
    gstApplicable: formConditions.gst_applicable !== false,   // default true
    international: !!formConditions.international,
    seatAllowed: formConditions.seat_ancillary_allowed !== false,
    baggageAllowed: formConditions.baggage_ancillary_allowed !== false,
    mealAllowed: formConditions.meal_ancillary_allowed !== false,
  };
};

/**
 * Full passenger record validator — context-aware via `formConditions`.
 * Returns `{ ok: bool, errors: [{field, msg}] }`.
 */
export const validatePassengerRecord = (pax, { travelDate, formConditions } = {}) => {
  const errors = [];
  const pt = (pax.type || pax.pt || 'ADULT').toUpperCase();
  const reqs = deriveFieldRequirements(formConditions, pt);

  if (!validateName(pax.firstName || pax.fN || '')) {
    errors.push({ field: 'firstName', msg: 'Enter a valid first name (letters only)' });
  }
  if (!validateName(pax.lastName || pax.lN || '')) {
    errors.push({ field: 'lastName', msg: 'Enter a valid last name (letters only)' });
  }
  if (reqs.dobRequired && !validateDOB(pax.dob, pt)) {
    errors.push({ field: 'dob', msg: `Valid date of birth is required for ${pt.toLowerCase()}` });
  }
  if (reqs.passportRequired) {
    if (!validatePassport(pax.passport || pax.pNum || '')) {
      errors.push({ field: 'passport', msg: 'Valid passport number is required' });
    }
    if (travelDate && !validatePassportExpiry(pax.passportExpiry || pax.eD, travelDate)) {
      errors.push({ field: 'passportExpiry',
                    msg: 'Passport must be valid for at least 6 months from travel' });
    }
  }
  return { ok: errors.length === 0, errors, reqs };
};
